import { useT } from '@/i18n/react';
import { getT } from '@/i18n';
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { Icon } from '@/components/Icon';
import './pullToRefresh.css';
import { REFHEX } from '@/config/gameplay';
import { createPullHold } from '@/lib/pullHold';

/**
 * Lehúzásra frissítés — a tartalom lecsúszik, mögötte hexagonok nőnek ki.
 *
 * A mozdulat a `BannerSlider` mintáját követi (irányfelismerés, majd
 * `setPointerCapture`), mert ugyanaz a probléma: egy lefelé induló mozdulat
 * lehet görgetés IS, és a kettőt csak az első néhány pixel után lehet
 * szétválasztani.
 *
 * ⚠️ A GESZTUS CSAK A LAP TETEJÉN INDULHAT. Ha a felhasználó már görgetett,
 * a lefelé húzás görgetés, nem frissítés — különben a lap közepén sem
 * lehetne visszafelé görgetni.
 *
 * A böngésző saját lehúzás-frissítésével nem kell versenyezni: a
 * `global.css` a `body`-n `overscroll-behavior-y: none`-t ír elő, tehát se
 * az Android Chrome húzása, se az iOS gumiszalagja nem szól bele.
 */

/** Ennél lejjebb húzva a felengedés frissít. */
const THRESHOLD_PX = 64;

/**
 * A húzás felső határa.
 *
 * Nem kemény vágás: a `1 - e^(-x/MAX)` görbe simán tart ide, tehát az ujj
 * nem ütközik falba, csak egyre nehezebben halad — ugyanaz az érzés, mint a
 * natív listák gumiszalagja.
 */
const MAX_PULL_PX = 120;

/**
 * Eddig a húzásig a méhsejt MÉG NEM LÁTSZIK.
 *
 * Holtsáv: egy koppintás vagy egy bizonytalan mozdulat ne villantsa fel a
 * hátteret. A hatszögek csak ezután kezdenek előjönni, és onnantól a húzás
 * teljes hátralévő részén (120-ig) telnek be — nem a frissítési küszöbig.
 */
const REVEAL_START_PX = 26;

/**
 * A visszacsúszás hossza — ADDIG kell a méhsejt-réteg.
 *
 * ⚠️ EGYEZNIE KELL a `.ptr--settling` átmenetének hosszával a CSS-ben. Ha
 * rövidebb, a méhsejt eltűnik ahelyett, hogy elhalványodna.
 */
const SETTLE_MS = 260;

/** Ennyi pixel elmozdulás után döntjük el, görgetés-e vagy húzás. */
const AXIS_DEADZONE_PX = 6;

/** A függőleges szándék ennyiszerese legyen a vízszintesnek. */
const DIRECTION_RATIO = 1.2;

interface DragState {
  x: number;
  y: number;
  axis: 'unknown' | 'pull' | 'other';
  /** Melyik ujjat követjük. `null` az egérnél. */
  touchId: number | null;
}

/**
 * Ennyi görgetésig még „a lap tetején vagyunk".
 *
 * ⚠️ NEM NULLA, és ez iOS miatt van. A WebView a gumiszalag után és
 * fülváltáskor is hagyhat törtpixeles görgetést (0,5–1 px), amit a
 * felhasználó nem lát. Szigorú `> 0` vizsgálattal a lehúzás ilyenkor
 * NEM INDULT EL, és ez kívülről véletlenszerű akadozásnak látszott.
 */
const AT_TOP_TOLERANCE_PX = 2;

export interface PullToRefreshProps {
  /** A frissítés. Amíg a promise fut, a jelző „frissít" állapotban marad. */
  onRefresh: () => Promise<unknown>;
  children: ReactNode;
  /** Optional hidden action after continuously holding an armed pull. */
  onLongPull?: () => void;
  disabled?: boolean;
}

export function PullToRefresh({ onRefresh, children, onLongPull, disabled = false }: PullToRefreshProps) {
  useT();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<DragState | null>(null);
  const pull = useRef(0);
  const longPullRef = useRef(onLongPull);
  longPullRef.current = onLongPull;
  const holdActionRef = useRef<() => void>(() => undefined);
  const hold = useMemo(() => createPullHold(REFHEX.unlockHoldMs, () => holdActionRef.current()), []);

  /*
    A frissítés REFBEN: a natív touch-figyelők egyszer kötődnek be, és egy
    prop-változás nem köthet újra mindent. Így mindig a legfrissebb `onRefresh`
    fut, de a figyelők a helyükön maradnak.
  */
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;
  const [settling, setSettling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  /**
   * Létezik-e egyáltalán a méhsejt-réteg.
   *
   * ⚠️ NEM ELÉG NULLÁRA HALVÁNYÍTANI. A `.ptr__field` rögzített elem, amit a
   * böngésző saját kompozit-rétegre tesz. Nullás átlátszóságnál a réteg
   * MEGMARAD, és a rajzolás nyomai ott ragadhatnak — Geri a bal felső
   * sarokban egy apró lila szilánkot látott a húzás után, pedig a DOM
   * állapota mérve teljesen visszaállt (minden változó 0, egyetlen látható
   * hatszög sem).
   *
   * Ha a réteg maga tűnik el, nincs mi beragadjon. Mozdulatonként két extra
   * renderbe kerül, és cserébe a Home nyugalmi állapotában eggyel kevesebb
   * kompozit-réteg van.
   */
  const [active, setActive] = useState(false);
  const settleTimer = useRef<number | undefined>(undefined);

  /**
   * A húzás mértéke CSS-VÁLTOZÓBAN, a gyökéren — nem React-állapotban.
   *
   * Két oka van, és mindkettő fontos:
   *
   * 1. ÁLLAPOT HELYETT: állapotként minden képkockán újrarajzolódna a teljes
   *    Home (köszöntő, összegző, bannersáv, feed). Így a mozdulat alatt
   *    EGYETLEN React-render sincs, csak két stílusírás.
   *
   * 2. A GYÖKÉREN, nem ezen az elemen: a fejléc a `PullToRefresh`-en KÍVÜL
   *    áll (testvérként), és neki is tudnia kell, mennyire van lehúzva —
   *    különben a saját tömör háttere eltakarná a mögötte megjelenő
   *    méhsejtet. A `:root`-ról mindkettő olvassa.
   */
  const setPull = useCallback((value: number) => {
    pull.current = value;
    const root = document.documentElement;
    root.style.setProperty('--ptr-y', `${value}px`);
    root.style.setProperty(
      '--ptr-p',
      String(
        Math.max(0, Math.min(1, (value - REVEAL_START_PX) / (MAX_PULL_PX - REVEAL_START_PX))),
      ),
    );
  }, []);

  // Képernyőváltáskor ne maradjon lehúzott állapot a gyökéren, és ne
  // fusson le egy időzítő egy már eltűnt komponensre.
  useEffect(
    () => () => {
      hold.cancel();
      window.clearTimeout(settleTimer.current);
      document.documentElement.style.removeProperty('--ptr-y');
      document.documentElement.style.removeProperty('--ptr-p');
    },
    [hold],
  );

  /*
    ── A mozdulat magja, bemeneti eszköztől függetlenül ──────────────────

    Ugyanez a három lépés fut az egérnél (pointer-események) és az ujjnál
    (natív touch-események). A kettőt azért kell szétválasztani, mert
    iOS-en a pointer-út NEM MŰKÖDIK — lásd a `useEffect` indoklását lentebb.
  */

  const beginGesture = useCallback(
    (x: number, y: number, touchId: number | null) => {
      // Görgetett lapon a lefelé húzás görgetés. És amíg egy frissítés fut,
      // nem indítunk másikat.
      if (window.scrollY > AT_TOP_TOLERANCE_PX || refreshing || disabled || document.hidden) return;
      hold.cancel();
      drag.current = { x, y, axis: 'unknown', touchId };
    },
    [refreshing, disabled, hold],
  );

  /** @returns true, ha MI visszük a mozdulatot (a hívó tiltsa le a görgetést). */
  const moveGesture = useCallback(
    (x: number, y: number): boolean => {
      const start = drag.current;
      if (!start) return false;

      const dx = x - start.x;
      const dy = y - start.y;

      if (start.axis === 'unknown') {
        /*
          ⚠️ A GÖRGETÉST MÁR A DÖNTÉS ELŐTT LE KELL TILTANI.

          A holtsáv 6 pixel, de a Chrome (és így az Android WebView) ennél
          hamarabb eldöntheti, hogy ez görgetés: onnantól a `touchmove` már
          NEM megszakítható, a `preventDefault()` hatástalan, és a
          pointer-események megszakadnak. Ha megvárnánk a holtsávot, a
          mozdulat már elveszett volna.

          Ezért lefelé tartó mozdulatnál AZONNAL blokkolunk, az első
          képpontnál. Kockázat nélkül: a gesztus eleve csak a lap tetején
          indulhat, ott pedig lefelé nincs mit görgetni. A vízszintes
          mozdulat (bannersáv lapozása) érintetlen marad, mert ott a
          `dy >= |dx|` nem teljesül.
        */
        const lefeleTart = dy > 0 && dy >= Math.abs(dx);

        if (Math.abs(dx) < AXIS_DEADZONE_PX && Math.abs(dy) < AXIS_DEADZONE_PX) {
          return lefeleTart;
        }
        const downwards = dy > 0 && dy > Math.abs(dx) * DIRECTION_RATIO;
        start.axis = downwards ? 'pull' : 'other';
        if (start.axis === 'other') {
          drag.current = null;
          return false;
        }
        setSettling(false);
        // A réteg CSAK MOST jön létre — nyugalmi állapotban nincs is.
        window.clearTimeout(settleTimer.current);
        setActive(true);
      }

      if (start.axis !== 'pull') return false;

      // Csillapítás: az első pixelek majdnem 1:1-ben követik az ujjat, aztán
      // egyre lassabban — a húzás így „nehezedik", nem ütközik falba.
      setPull(dy <= 0 ? 0 : MAX_PULL_PX * (1 - Math.exp(-dy / MAX_PULL_PX)));
      hold.update(Boolean(longPullRef.current) && pull.current >= THRESHOLD_PX);
      return true;
    },
    [setPull, hold],
  );

  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    // ⚠️ AZ UJJAT NEM ITT KEZELJÜK — lásd a natív touch-figyelőt lentebb.
    // Kétszeres kezelés esetén a húzás duplán számolna.
    if (event.pointerType === 'touch' || event.button !== 0) return;
    beginGesture(event.clientX, event.clientY, null);
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>) {
    if (event.pointerType === 'touch') return;
    const elozoTengely = drag.current?.axis;
    if (!moveGesture(event.clientX, event.clientY)) return;
    /*
      A capture azért kell, hogy az egér kifuthasson az elemből (vagy az
      ablakon túlra), és a felengedést akkor is megkapjuk — enélkül a
      tartalom lehúzva ragadna. Csak egyszer, amikor átvettük a mozdulatot.
    */
    if (elozoTengely === 'unknown') event.currentTarget.setPointerCapture(event.pointerId);
  }

  const endGesture = useCallback((refresh = true) => {
    hold.cancel();
    const start = drag.current;
    drag.current = null;
    if (!start || start.axis !== 'pull') return;

    const armed = pull.current >= THRESHOLD_PX;
    // A tartalom MINDEN esetben azonnal visszaugrik — Geri kérése. A
    // frissítés a háttérben fut, a lap nem marad lehúzva.
    setSettling(true);
    setPull(0);

    /*
      A réteget csak a visszacsúszás UTÁN bontjuk le — a 260 ms a
      `.ptr--settling` átmenetének hossza (lásd a CSS-t). Korábban levéve a
      méhsejt nem elhalványodna, hanem eltűnne.
    */
    window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(() => setActive(false), SETTLE_MS);

    if (!armed || !refresh) return;

    setRefreshing(true);
    void Promise.resolve(refreshRef.current())
      .catch(() => {
        /* A frissítés hibáját a frissített felület mutatja meg, nem ez. */
      })
      .finally(() => setRefreshing(false));
  }, [setPull, hold]);

  holdActionRef.current = () => {
    if (document.hidden || disabled || drag.current?.axis !== 'pull'
      || pull.current < THRESHOLD_PX || !longPullRef.current) return;
    endGesture(false);
    longPullRef.current();
  };

  useEffect(() => {
    const cancel = () => endGesture(false);
    const visibility = () => { if (document.hidden) cancel(); };
    window.addEventListener('blur', cancel);
    document.addEventListener('visibilitychange', visibility);
    if (disabled) cancel();
    return () => {
      window.removeEventListener('blur', cancel);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [disabled, endGesture]);

  function endDrag(event: PointerEvent<HTMLDivElement>) {
    if (event.pointerType === 'touch') return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    endGesture(event.type !== 'pointercancel');
  }

  /**
   * ⚠️ AZ UJJHOZ NATÍV, NEM-PASSZÍV `touchmove` KELL — IOS-EN A POINTER-ÚT
   * NEM MŰKÖDIK.
   *
   * Geri iOS-es hibajelentése (1.10.0, iOS 18.7, build 72) ezt feketén-fehéren
   * megmutatta: a naplóban MINDEN lenyomást azonnal `pointercancel` követett,
   * és egyetlen `pointermove` sem jött közben —
   *
   *     ujj: le · 1 · 205,148 → home__greet-row
   *     ujj: megszakadt · 1 · 0,0 → ptr
   *
   * Ez a WKWebView viselkedése: amint a gesztust görgetésnek ítéli, ELVESZI,
   * és a pointer-eseményeket megszakítja. A `pointercancel` nálunk a mozdulat
   * végét jelenti, ezért a lehúzás elindult, majd azonnal visszaugrott —
   * pontosan, ahogy Geri leírta.
   *
   * A görgetés átvételét csak a `touchmove` `preventDefault()`-ja akadályozza
   * meg. React `onTouchMove`-val ez nem megy: a React a `touchmove`-ot
   * PASSZÍVAN köti be, passzív figyelőben pedig a `preventDefault()` nem
   * érvényesül (a böngésző figyelmen kívül hagyja). Ezért kell natív
   * `addEventListener`, kifejezett `{ passive: false }`-szal.
   *
   * Az egér marad a pointer-úton: ott a capture kell ahhoz, hogy a mutató
   * kifuthasson az ablakból.
   */
  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;

    /** A mozdulatot indító ujj megkeresése a listában. */
    const sajatUjj = (list: TouchList): Touch | null => {
      const id = drag.current?.touchId;
      if (id === undefined || id === null) return null;
      for (let i = 0; i < list.length; i += 1) {
        const touch = list[i];
        if (touch && touch.identifier === id) return touch;
      }
      return null;
    };

    const onTouchStart = (event: TouchEvent) => {
      /*
        ⚠️ EGY MÁSIK UJJ NEM SZAKÍTJA MEG A MOZDULATOT.

        Korábban a kezelő kiszállt, ha egynél több ujj volt a képernyőn —
        csakhogy iOS-en a tenyér széle is ujjnak számít, és Geri naplójában
        többször látszik `le · 2` közvetlenül a lehúzás közben. Attól kezdve
        a húzás nem folytatódott. Most az ELSŐ ujjat azonosító szerint
        követjük, a többit egyszerűen figyelmen kívül hagyjuk.
      */
      if (drag.current) return;
      const touch = event.changedTouches[0];
      if (!touch) return;
      // Csak a mi felületünkön indított mozdulat számít (a dokk, a lapok és
      // a rétegek fölött ne induljon lehúzás).
      if (!(touch.target instanceof Node) || !node.contains(touch.target)) return;
      beginGesture(touch.clientX, touch.clientY, touch.identifier);
    };

    const onTouchMove = (event: TouchEvent) => {
      const touch = sajatUjj(event.touches);
      if (!touch) return;
      // A `preventDefault()` CSAK akkor, ha tényleg mi visszük: különben a
      // szokásos függőleges görgetést is megbénítanánk a lap tetején.
      if (moveGesture(touch.clientX, touch.clientY)) event.preventDefault();
    };

    const onTouchEnd = (event: TouchEvent) => {
      if (!sajatUjj(event.changedTouches)) return;
      endGesture(event.type !== 'touchcancel');
    };

    /*
      ⚠️ A DOKUMENTUMON, LEFELÉ HALADÓ (capture) FÁZISBAN.

      Így a mozdulatot azelőtt látjuk, hogy bármelyik alattunk lévő elem
      hozzáférne — és az ujj akkor is követhető marad, ha közben kifut a
      `.ptr` dobozából. Geri iOS-en pontosan azt látta, hogy a képernyő
      szélén (üres területen) megy a lehúzás, a tartalom fölött viszont
      akadozik; az elemhez kötött figyelő ezt magyarázza.

      A `touchmove` `{ passive: false }`-szal megy, különben a
      `preventDefault()` hatástalan — lásd a fenti hosszabb indoklást.
    */
    const opts = { capture: true } as const;
    document.addEventListener('touchstart', onTouchStart, { ...opts, passive: true });
    document.addEventListener('touchmove', onTouchMove, { ...opts, passive: false });
    document.addEventListener('touchend', onTouchEnd, opts);
    document.addEventListener('touchcancel', onTouchEnd, opts);

    return () => {
      document.removeEventListener('touchstart', onTouchStart, opts);
      document.removeEventListener('touchmove', onTouchMove, opts);
      document.removeEventListener('touchend', onTouchEnd, opts);
      document.removeEventListener('touchcancel', onTouchEnd, opts);
    };
  }, [beginGesture, moveGesture, endGesture]);

  return (
    <div
      ref={rootRef}
      className={`ptr${settling ? ' ptr--settling' : ''}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      {/* A réteg csak a mozdulat idejére létezik — az indoklás az `active`
          állapotnál, mérési előzménnyel. */}
      {active || refreshing ? (
        <div className="ptr__field" aria-hidden="true">
          <HexField />
        </div>
      ) : null}

      {/*
        A FRISSÍTÉS IKONJA — a húzással EGYÜTT fordul (Geri, 2026-09-16).

        Nem külön animáció: az elfordulás szöge közvetlenül a `--ptr-p`-ből
        jön, tehát pontosan követi az ujjat, és visszafelé is fordul, ha a
        felhasználó meggondolja magát. Amíg a frissítés fut, átvált saját
        ütemű, folyamatos forgásra — akkor már nincs ujj, amit követhetne.

        Ugyanaz a „magába forduló nyíl", ami a bezárt kört jelöli a
        részletezőn (`Icon name="loop"`) — a jel a saját készletünkből jön,
        nem külön rajz.
      */}
      {active || refreshing ? (
        <div
          className={`ptr__spinner${refreshing ? ' ptr__spinner--busy' : ''}`}
          aria-hidden="true"
        >
          <Icon name="loop" size={24} />
        </div>
      ) : null}

      {/* A felolvasó a frissítés TÉNYÉT kapja meg; a hexagonok neki semmit
          nem mondanak, ezért azok `aria-hidden`-ek. */}
      <p className="ptr__status" role="status">
        {refreshing ? getT().remainingUI.refreshing : ''}
      </p>

      <div className="ptr__content">{children}</div>
    </div>
  );
}

/*
  ── A méhsejt geometriája ──────────────────────────────────────────────

  ⚠️ EZEK NEM SZABADON HANGOLHATÓ SZÁMOK. Egy hegyes tetejű (pointy-top)
  hatszög akkor és csak akkor illeszkedik oldalával a szomszédjához, ha:

    szélesség (lapos oldalak közt) = √3 · R
    magasság  (csúcstól csúcsig)   = 2 · R
    oszlopköz = szélesség
    sorköz    = 1,5 · R          (NEM a teljes magasság — a sorok egymásba érnek)
    a páratlan sorok fél szélességgel eltolva

  Az első változatban ezek közelítő szorzók voltak (0,78 és 0,86), és emiatt
  a hatszögek nem értek össze: rés maradt köztük. Itt a pontos képlet megy.
*/
const HEX_R = 11;
const HEX_W = Math.sqrt(3) * HEX_R;
const HEX_ROW_STEP = 1.5 * HEX_R;
/**
 * A rács mérete — a képernyő FELSŐ SÁVJÁRA (28vh), nem az egészre.
 *
 * Az oszlopszám szabja meg, mekkorák lesznek a hatszögek: a `slice` a
 * szélességhez igazít, tehát 13 oszlop egy 375 pixeles telefonon nagyjából
 * 29 pixel széles hatszögeket ad — Geri kérésére 25%-kal nagyobbak, mint a
 * korábbi 16 oszlopos rács. A `HEX_R` ilyenkor NEM változik: a sugár csak a
 * viewBox arányait adja, a képernyőméretet az oszlopszám.
 *
 * ⚠️ A SORSZÁM EGYÜTT MOZOG AZ OSZLOPSZÁMMAL. A `slice` a nagyobbik
 * arányhoz igazít; ha a viewBox túl alacsony lenne a 38vh-s sávhoz képest,
 * a magasság venné át a méretezést, és a hatszögek kinagyítódnának. 13
 * oszlophoz 12 sor az a minimum, ami mellett a szélesség marad a mérvadó.
 */
const HEX_ROWS = 8;
/** A páros sorok hossza; a páratlanok eggyel rövidebbek, fél cellával eltolva. */
const HEX_COLS = 8;

/**
 * Ennyi lépésben gyullad meg a méhsejt a középvonaltól kifelé.
 *
 * Nem darabonként — az mérhetően akadt (lásd a `HexField` indoklását).
 * Tizennégy lépés elég sűrű ahhoz, hogy a terjedés folyamatosnak lássék, és
 * elég kevés ahhoz, hogy a húzás alatt csak ennyi elem stílusa számolódjon
 * újra képkockánként.
 */
const BAND_COUNT = 10;

/**
 * Ennyi sorral és oszloppal rajzolunk többet a látható sávnál.
 *
 * A méhsejtnek nem lehet látható SZÉLE: a rács minden irányban túlnyúlik a
 * viewBoxon, és a `slice` vágja le.
 *
 * EGY sor/oszlop elég, és ez nem szőrszálhasogatás: kettővel a rács 380
 * elemre hízott, és a rajzolás mérhetően akadt. A fél cellás eltolást egy
 * sor is lefedi.
 */
const OVERSCAN = 1;


/**
 * Mennyit nyom a latban a VÍZSZINTES távolság a megjelenés sorrendjében.
 *
 * Kicsi szám, és ez a lényeg: a terjedés felül középről indul, de oldalra
 * sokkal gyorsabban fut, mint lefelé. Így a felső sor szinte azonnal kiér a
 * szélekig, és nem marad sötét sarok — az okozta a „lekerekítés" látszatát
 * (lásd a `HexField` indoklását).
 */
const HORIZONTAL_WEIGHT = 0.18;

/**
 * A hatszögek mezője.
 *
 * A megjelenés SORRENDJE a mező közepétől kifelé halad, tehát a méhsejt
 * középről nő ki, nem a sarkából. Minden hatszög SAJÁT alap-átlátszóságot is
 * kap: tökéletesen illeszkedő, azonos színű hatszögekből egyetlen tömör folt
 * lenne, a szabálytalan átlátszóság viszont megmutatja a rácsot.
 */
function HexField() {
  const cells: { row: number; col: number; x: number; y: number; order: number; phase: number }[] = [];

  const fieldWidth = (HEX_COLS - 1) * HEX_W;
  const fieldHeight = (HEX_ROWS - 1) * HEX_ROW_STEP;
  /*
    ⚠️ A RÁCS TÚLLÓG A LÁTHATÓ SÁVON, MINDEN OLDALON.

    A `OVERSCAN` sorral-oszloppal többet rajzolunk, mint amennyi a viewBoxba
    fér. Enélkül a méhsejt SZÉLE látszott: a bal és jobb peremen a páratlan
    sorok fél cellával beljebb kezdődnek, alul-fölül pedig egyszerűen véget
    ért a rács — és a húzás közben pontosan ez a perem hívta fel magára a
    figyelmet (Geri, 2026-09-16). A viewBox változatlan, a fölösleg a
    `slice` miatt levágódik.
  */
  const centerX = fieldWidth / 2;

  for (let row = -OVERSCAN; row < HEX_ROWS + OVERSCAN; row += 1) {
    const odd = Math.abs(row % 2) === 1;
    for (let col = -OVERSCAN; col < HEX_COLS + OVERSCAN; col += 1) {
      const x = col * HEX_W + (odd ? HEX_W / 2 : 0);
      const y = row * HEX_ROW_STEP;

      cells.push({
        row,
        col,
        x,
        y,
        /*
          A MEGJELENÉS GÓCPONTJA A MEZŐ FELSŐ KÖZEPE: onnan terjed lefelé és
          oldalra (Geri, 2026-09-16).

          ⚠️ A VÍZSZINTES TERJEDÉS SOKKAL GYORSABB A FÜGGŐLEGESNÉL, és ez
          nem esztétikai finomhangolás. Előbb sugárirányú (`hypot`) távolság
          döntött, vagyis a front KÖR alakú volt. Félúton emiatt a két felső
          sarok még sötéten állt, miközben a közép már világított — és ez
          pontosan úgy nézett ki, mintha a méhsejtnek lekerekített sarka
          lenne. (Geri kétszer is ezt jelezte, és igaza volt: a teljes
          lehúzásnál eltűnt, mert akkor már minden sáv égett.)

          A `HORIZONTAL_WEIGHT` ezért csak töredékkel számít bele: a felső
          sor szinte azonnal végigfut a szélekig, a front pedig lapos marad
          és lefelé halad. A középről indulás így is látszik, sarok viszont
          nem marad le.

          A SZÓRÁS SZÁNDÉKOSAN NAGY: Geri kérése, hogy ne tökéletesen egymás
          utáni hatszögek gyulladjanak meg. Ekkora zajjal a front
          szabálytalanul kúszik — egy-egy darab előreszalad, más lemarad.
        */
        order:
          y / fieldHeight +
          (Math.abs(x - centerX) / (fieldWidth / 2)) * HORIZONTAL_WEIGHT +
          (pseudoRandom(row * 7 + col * 13 + 5) - 0.5) * 0.22,
        // A lélegzés fázisát külön menet osztja ki, mert SZOMSZÉDFÜGGŐ
        // (lásd `assignPhases`) — cellánként eldönteni nem lehetne.
        phase: 0,
      });
    }
  }

  assignPhases(cells);

  /*
    ⚠️ A HATSZÖGEK SÁVOKBA CSOPORTOSULNAK — ÉS EZ MÉRÉS EREDMÉNYE.

    Először minden hatszög maga számolta a megjelenését a gyökéren álló
    `--ptr-p`-ből. Egy gyökér-változó módosítása viszont ÉRVÉNYTELENÍTI a
    stílust minden elemnél, amelyik hivatkozik rá — 233 hatszögnél ez
    képkockánként 233 újraszámolás.

    MÉRVE (asztali gép, valódi képkocka-idők, 85 minta): medián 16,7 ms,
    p95 25,1 ms, és 32 képkocka lépte túl a 20 ms-ot. A 60 fps kerete
    16,7 ms — vagyis már asztalon is akadt, telefonon sokkal rosszabb lenne.

    Így viszont a megjelenést `BAND_COUNT` darab `<g>` viszi: csak ennyi
    elem függ a húzástól, a 233 hatszög átlátszósága állandó. A látvány
    ugyanaz — a sávok középről kifelé, egymás után gyulladnak meg —, mert
    a szórás miatt a sávhatárok amúgy sem esnek egy vonalba.
  */
  /*
    ⚠️ A NORMALIZÁLÁS A MINIMUMTÓL MEGY, NEM NULLÁTÓL.

    A túllógó sorok (`OVERSCAN`) miatt a rendezési érték NEGATÍV is lehet —
    a viewBox fölé rajzolt sorok `y`-ja kisebb nullánál. Ha csak a
    maximummal osztanánk, ezekre negatív sávindex jönne ki, és a `bands`
    tömbben nem létező rekeszbe kerülnének. (Élesben ez azonnal el is
    szállt: „Cannot read properties of undefined (reading 'push')".)
  */
  const orders = cells.map((cell) => cell.order);
  const minOrder = Math.min(...orders);
  const span = Math.max(...orders) - minOrder || 1;

  const bands: (typeof cells)[] = Array.from({ length: BAND_COUNT }, () => []);
  for (const cell of cells) {
    const ratio = (cell.order - minOrder) / span;
    const index = Math.max(0, Math.min(BAND_COUNT - 1, Math.floor(ratio * BAND_COUNT)));
    bands[index]!.push(cell);
  }

  const left = -HEX_W / 2;
  const top = -HEX_R;
  return (
    <svg
      className="ptr__hexes"
      viewBox={`${left} ${top} ${fieldWidth - 2 * left} ${fieldHeight - 2 * top}`}
      /*
        `slice`, nem `meet`: a mező KITÖLTI a sáv szélességét, és ami nem fér
        ki függőlegesen, az levágódik. `meet` esetén a keskeny sávban a
        magasság szabná meg a méretet, és a hatszögek a húzás elején
        parányira zsugorodnának, majd nőnének — itt viszont végig azonos,
        nagy méretben állnak, és a sáv egyre többet MUTAT meg belőlük.

        `YMid`: a sáv a mező KÖZEPÉT mutatja, és növekedve fölfelé és lefelé
        egyszerre fedi fel — ezért tud a méhsejt középről minden irányba
        terjedni.
      */
      preserveAspectRatio="xMidYMid slice"
    >
      {bands.map((band, index) => (
        <g
          key={index}
          className="ptr__band"
          style={
            {
              /* Mikor KEZD megjelenni ez a sáv, 0–1 között. A 0,75-ös
                 szorzó hagy helyet a felfutásnak: a legkülső sáv is beér,
                 mire a húzás a végére ér. */
              '--d': ((index / BAND_COUNT) * 0.75).toFixed(3),
            } as React.CSSProperties
          }
        >
          {band.map((cell) => (
            <use
              key={`${cell.row},${cell.col}`}
              href="#ptr-hex"
              x={cell.x}
              y={cell.y}
              className="ptr__hex"
              style={
                {
                  /* A lélegzés fázisa, 0–1. Szomszédfüggő (lásd
                     `assignPhases`), tehát két egymás melletti darab sosem
                     ér egyszerre csúcsra. */
                  '--phase': cell.phase,
                } as React.CSSProperties
              }
            />
          ))}
        </g>
      ))}
      <defs>
        {/* Egyetlen hatszög, sokszor felhasználva: a `use` a legolcsóbb mód
            ugyanazt az alakzatot százszor kirakni. */}
        <path id="ptr-hex" d={hexPathAt(0, 0, HEX_R)} />
      </defs>
    </svg>
  );
}

/**
 * Ennyi ELTOLT FÁZISBA soroljuk a hatszögeket.
 *
 * ⚠️ ÖT, ÉS NEM KEVESEBB. Egy hatszögnek hat szomszédja van; ahhoz, hogy
 * soha ne kerüljön kettő azonos fázisba egymás mellé, elvileg három is elég
 * volna (a méhsejtrács háromszínezhető) — csakhogy a háromszínezés
 * szabályos, ismétlődő mintát rajzol. Öttel a mohó kiosztás mindig talál
 * szabad sávot, és a mintázat szabálytalan marad.
 */
const PHASE_SLOTS = 5;

/**
 * ⚠️ KÉT SZOMSZÉDOS HATSZÖG SOSEM JÁR EGYÜTT (Geri, 2026-09-16).
 *
 * Eredetileg a SZÍNÜK volt különböző. Amióta minden darab végigjárja a
 * 0,2–1,0 tartományt, a szín önmagában nem elég: két szomszéd akkor
 * olvadna össze, ha a lélegzésük EGYSZERRE érne csúcsra. Ezért ugyanaz a
 * mohó szabály most a FÁZISRA vonatkozik.
 *
 * Mohó színezés: a cellákat sorban végigjárva mindegyik olyan fázissávot
 * kap, amit egyik MÁR KIOSZTOTT szomszédja sem visel. Öt sáv kell, nem
 * három: a méhsejtrács elvileg háromszínezhető, de az szabályos, ismétlődő
 * mintát adna — ötből választva a mintázat szabálytalan marad.
 *
 * A sávon BELÜL még egy kis szórás is van, hogy két azonos sávba eső (nem
 * szomszédos) darab se induljon pontosan együtt.
 *
 * A hatszögrács szomszédsága offszetes sorokkal: a soron belüli két szomszéd
 * mellett a fölötte és alatta lévő sorból kettő-kettő — és hogy melyik
 * kettő, az attól függ, hogy a sor el van-e tolva.
 */
function assignPhases(cells: { row: number; col: number; phase: number }[]): void {
  const kulcs = (row: number, col: number) => `${row},${col}`;
  const kiosztott = new Map<string, number>();

  for (const cell of cells) {
    const odd = Math.abs(cell.row % 2) === 1;
    // Az eltolt sorokban a szomszéd oszlopai eggyel jobbra csúsznak.
    const el = odd ? 0 : -1;
    const szomszedok: [number, number][] = [
      [cell.row, cell.col - 1],
      [cell.row, cell.col + 1],
      [cell.row - 1, cell.col + el],
      [cell.row - 1, cell.col + el + 1],
      [cell.row + 1, cell.col + el],
      [cell.row + 1, cell.col + el + 1],
    ];

    const tiltott = new Set<number>();
    for (const [r, c] of szomszedok) {
      const sav = kiosztott.get(kulcs(r, c));
      if (sav !== undefined) tiltott.add(sav);
    }

    const szabad: number[] = [];
    for (let i = 0; i < PHASE_SLOTS; i += 1) if (!tiltott.has(i)) szabad.push(i);

    /*
      Elvileg mindig marad szabad sáv (öt sáv, és sorfolytonosan haladva
      legfeljebb három szomszéd van már kiosztva). A tartalék ág csak azért
      van itt, hogy egy későbbi rácsméret-változás se csempészhessen
      `undefined`-ot a fázisba.
    */
    const zaj = pseudoRandom(cell.row * 17 + cell.col * 5 + 11);
    const sav = szabad.length
      ? szabad[Math.floor(pseudoRandom(cell.row * 31 + cell.col * 7 + 3) * szabad.length)] ?? szabad[0]!
      : 0;

    kiosztott.set(kulcs(cell.row, cell.col), sav);
    // A sáv a ciklus egy ötödét foglalja; a zaj ezen belül mozgat.
    cell.phase = Number(((sav + zaj * 0.8) / PHASE_SLOTS).toFixed(3));
  }
}



/** Hegyes tetejű hatszög a megadott pont köré, részútvonalként. */
function hexPathAt(cx: number, cy: number, r: number): string {
  const points = Array.from({ length: 6 }, (_, i) => {
    // -90°-tól indulva: az első csúcs FELÜL van (pointy-top).
    const angle = (Math.PI / 180) * (60 * i - 90);
    // Két tizedes elég: a mező a képernyőn kb. kétszeres nagyításban áll,
    // tehát a századpixel már a raszterező alatt van — a hosszabb szám
    // viszont a `d` attribútumot hizlalná.
    return `${(cx + r * Math.cos(angle)).toFixed(2)} ${(cy + r * Math.sin(angle)).toFixed(2)}`;
  });
  return `M${points.join('L')}Z`;
}

/** Stabil ál-véletlen 0 és 1 között — ugyanaz a mag ugyanazt adja. */
function pseudoRandom(seed: number): number {
  const value = Math.sin(seed * 12.9898) * 43758.5453;
  return value - Math.floor(value);
}
